import { Link } from '@tanstack/react-router';

import { QUEUE_STATUS_STYLE, useQueueHealth } from '@/features/dataops/queue-health';

/**
 * Small pill that turns amber/red when background workers are backed up.
 * Present on every screen so a stalled scoring queue is visible from anywhere,
 * and links straight to Data Ops.
 */
export function QueueHealthPill({ compact = false }: { compact?: boolean }) {
  const { data, isPending, isError } = useQueueHealth();

  // An unreachable health endpoint is itself a degraded signal — never render
  // this as healthy just because the check failed.
  const style = isError
    ? QUEUE_STATUS_STYLE.backed_up
    : QUEUE_STATUS_STYLE[data?.status ?? 'healthy'];

  const label = isError ? 'queue health unknown' : isPending ? 'checking queues' : style.label;

  const failing = data?.queues.filter((queue) => queue.failed > 0).length ?? 0;
  const title = isError
    ? 'Cannot reach the queue-health endpoint'
    : failing > 0
      ? `${String(failing)} queue(s) reporting failures`
      : 'Background worker health';

  return (
    <Link
      to="/data-ops"
      title={title}
      aria-label={`Background worker health: ${label}`}
      className="flex h-8 flex-none items-center gap-[7px] rounded-2xl px-2.5 no-underline hover:no-underline"
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <span
        aria-hidden
        className="motion-safe:animate-mesh-pulse size-[7px] flex-none rounded-full"
        style={{ background: style.dot }}
      />
      {!compact && (
        <span className="font-mono text-[11px] tracking-[0.02em]" style={{ color: style.text }}>
          {label}
        </span>
      )}
    </Link>
  );
}
